import React, { useState, useEffect } from 'react';
import { Collapse, Modal as AntdModal, Tabs } from 'antd';
import { Modal, Button, Tag, ComponentAccessCheck } from 'ui';
import coursesApiClient from 'utils/coursesApiClient';
import useHandleError from 'utils/useHandleError';
import Spinner from 'components/Spinner';
import useQueryApiClient from 'utils/useApiClient';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import generalHelpers from 'utils/generalHelpers';
import { connect } from 'react-redux';
import moment from 'moment';
import { config } from 'config/config';
import { useT } from 'hooks/Locale/useT';
import SessionsTable from '../ResultTables/SessionsTable';
import LearningTestTable from '../ResultTables/LearningTestTable';
import ScormTestTable from '../ResultTables/ScormTestTable';
import AssessmentTable from '../ResultTables/AssessmentTable';
import LearningTable from '../ResultTables/LearningTable';
import AssignmentTable from '../ResultTables/AssignmentTable';
import CertificateTable from '../ResultTables/CertificateTable';
import ActivitiesChangeModal from './ActivitiesChangeModal';
import SessionAttendanceChangeModal from './SessionAttendanceChangeModal';
import PageUnderConstruction from 'pages/PageUnderConstruction';
import { TagStatus } from 'ui/Tag/types';

interface ResultsModalInterface {
    open: boolean;
    onCancel: React.Dispatch<React.SetStateAction<boolean>>;
    programId: number;
    activity: {
        enrollmentId: number;
        activityId: number;
        fullName: string;
        title: string;
        type: string;
    };
    session?: any;
}

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    };
};

const ResultModal: React.FC<ResultsModalInterface> = ({
    open,
    onCancel,
    activity,
    programId,
    session,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [activityData, setActivityData] = useState();
    const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
    const [activitiesModalOpen, setActivitiesModalOpen] = useState(false);
    const { Panel } = Collapse;
    const [handleError] = useHandleError();
    const { confirm } = AntdModal;
    const t = useT();

    useEffect(() => {
        if (activity.activityId !== 0) {
            loadActivity();
        }
    }, [activity]);

    const loadActivity = async () => {
        setIsLoading(true);
        try {
            const response = await coursesApiClient.request(
                `/api/v1/courses/learning-program/results/${activity.enrollmentId}/activity/${activity.activityId}`,
                [],
                'GET',
            );

            setActivityData(response.attempt);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const { appendData: cancelDeletion, isLoading: isCancelLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/${programId}/attempt/:id/cancel`,
            method: 'POST',
        },
        onSuccess: () => {
            loadActivity();
        },
    });

    const { appendData: initDeletion, isLoading: isInitLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/${programId}/attempt/:id/init`,
            method: 'POST',
        },
        onSuccess: () => {
            loadActivity();
        },
    });

    const genExtra = (attempt: any) => {
        const hasPermissions = generalHelpers.hasSpecialPermissions(session, 'remove_attempt');

        return (
            <div className="flex align-center gap-10">
                {hasPermissions && (
                    <>
                        <div className="bold">id: {attempt.id}</div>
                        {!!attempt.deletionInitiated ? (
                            <Button
                                loading={isCancelLoading}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    cancelDeletion({}, { id: attempt.id });
                                }}
                                message="program.cancel_deletion"
                            />
                        ) : (
                            <Button
                                loading={isInitLoading}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    warnDeletion({}, { id: attempt.id }, { id: attempt.id });
                                }}
                                message="program.init_deletion"
                            />
                        )}
                    </>
                )}
            </div>
        );
    };

    const warnModalData = (data: any) => {
        return (
            <div>
                {t('program.delete_attempt_warning')}
                <table>
                    <tbody>
                        <tr className="p-2">
                            <td>{t('program.activity_data')}: </td>
                            <td>
                                <strong>{data.activity}</strong>
                            </td>
                        </tr>
                        <tr className="p-2">
                            <td>{t('program.attempt_data')}: </td>
                            <td>
                                <strong>{data.attempt}</strong>
                            </td>
                        </tr>
                        <tr className="p-2">
                            <td>{t('program.certificate_data')}: </td>
                            <td>
                                <strong>{data.certificate}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const { appendData: warnDeletion } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `/api/v1/courses/learning-program/${programId}/attempt/:id/data`,
            method: 'GET',
        },
        onSuccess: (response, passOnSuccess) => {
            confirm({
                title: t('general.attention'),
                content: warnModalData(response.data),
                icon: <ExclamationCircleOutlined />,
                okText: t('general.yes'),
                cancelText: t('general.no'),

                onOk() {
                    initDeletion({}, { id: passOnSuccess.id });
                },
            });

            loadActivity();
        },
    });

    const renderTable = (type: string, data: any) => {
        switch (type) {
            case 'LECTURE':
                return <SessionsTable data={data} />;
            case 'LEARNING_TEST':
                return <LearningTestTable data={data} />;
            case 'SCORM_TEST':
                return <ScormTestTable data={data} />;
            case 'ASSESSMENT':
                return <AssessmentTable data={data} onReload={loadActivity} />;
            case 'ASSIGNMENT':
                return <AssignmentTable data={data} onReload={loadActivity} />;
            case 'CERTIFICATE':
                return <CertificateTable data={data} onReload={loadActivity} />;
            case 'SCORM_LEARNING':
            case 'PDF_MATERIAL':
            case 'VIDEO_MATERIAL':
            case 'EMBEDDED_DOC':
                return <LearningTable data={data} />;
            default:
                return <PageUnderConstruction />;
        }
    };

    return (
        <>
            <Modal
                open={open}
                width={'80%'}
                onCancel={() => onCancel(false)}
                className="add_remove_activity"
                title={activity.title}
                destroyOnClose={true}
                footer={<Button onClick={() => onCancel(false)} message="general.close" />}
            >
                <Spinner spinning={isLoading}>
                    <div className="bold flex justify-between mb-4">
                        {activity.fullName}
                        {generalHelpers.hasSpecialPermissions(session, 'edit_terms') ? (
                            <>
                                {activity.type === 'LECTURE' ? (
                                    <Button
                                        onClick={() => setAttendanceModalOpen(!attendanceModalOpen)}
                                        message="program.edit_attendances"
                                    />
                                ) : (
                                    <Button
                                        onClick={() => setActivitiesModalOpen(!activitiesModalOpen)}
                                        message="program.edit_activities"
                                    />
                                )}
                            </>
                        ) : null}
                    </div>
                    {activityData ? (
                        <Collapse>
                            {Object.values(activityData)
                                .sort((a: any, b: any) => b.attempt - a.attempt)
                                .map((result: any) => {
                                    return (
                                        <Panel
                                            header={
                                                <div className="flex gap-5 align-center">
                                                    <p className="m-0">
                                                        {t('program.enrollment_date', {
                                                            date: moment(result.createdAt).format(
                                                                config.defaultDateFormat,
                                                            ),
                                                        })}
                                                    </p>
                                                    {result.status === 'COMPLETED' ? (
                                                        <p className="m-0">
                                                            {t('general.is_completed')}{' '}
                                                            {moment(result.isCompleted).format(
                                                                config.defaultDateFormat,
                                                            )}
                                                        </p>
                                                    ) : (
                                                        <Tag status={result.status as TagStatus} />
                                                    )}
                                                </div>
                                            }
                                            extra={genExtra(result)}
                                            key={result.id}
                                        >
                                            <Tabs
                                                destroyInactiveTabPane
                                                items={Object.entries(result.data).map(
                                                    (entries: any) => {
                                                        return {
                                                            label: t(
                                                                'program.activities.' +
                                                                    entries[0].toLocaleLowerCase(),
                                                            ),
                                                            key: entries[0],
                                                            children: renderTable(
                                                                entries[0],
                                                                entries[1],
                                                            ),
                                                        };
                                                    },
                                                )}
                                            />
                                        </Panel>
                                    );
                                })}
                        </Collapse>
                    ) : null}
                </Spinner>
            </Modal>
            <SessionAttendanceChangeModal
                open={attendanceModalOpen}
                onCancel={setAttendanceModalOpen}
                id={activity.enrollmentId}
                onSuccess={loadActivity}
            />
            <ActivitiesChangeModal
                open={activitiesModalOpen}
                onCancel={setActivitiesModalOpen}
                id={activity.enrollmentId}
                activityId={activity.activityId}
                onSuccess={loadActivity}
            />
        </>
    );
};

export default connect(mapStateToProps)(ResultModal);
